import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, PressableBox, TextInput } from "@opendoor/bricks-next";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/core/AutocompleteInput/AutocompleteInput.tsx
var ListBoxElement = React.forwardRef(function ListBoxElement(props, forwardedRef) {
	return /* @__PURE__ */ jsx("ul", {
		...props,
		ref: forwardedRef
	});
});
var BricksNextListBox = ({ children, listBoxId, ariaLabel = "suggestions", size = "medium" }) => {
	const borderRadius = {
		small: "$7x",
		medium: "$9x",
		large: "$12x"
	}[size] || "$9x";
	return /* @__PURE__ */ jsx(Box, {
		borderBottomLeftRadius: borderRadius,
		borderBottomRightRadius: borderRadius,
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
		maxHeight: 450,
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		marginTop: -1.5,
		borderLeftWidth: 1.5,
		borderRightWidth: 1.5,
		borderBottomWidth: 1.5,
		borderTopWidth: 0,
		borderStyle: "solid",
		borderColor: "$borderAccentBrand",
		backgroundColor: "$backgroundPrimary",
		overflow: "hidden",
		zIndex: 1e3,
		children: /* @__PURE__ */ jsx(ListBoxElement, {
			role: "listbox",
			"aria-label": ariaLabel,
			id: listBoxId,
			style: {
				padding: 0,
				margin: 0,
				listStyleType: "none"
			},
			children
		})
	});
};
var AutocompleteInput = ({ id, label, hideLabel, placeholder, disabled, items, searchTerm, onSearchChange, onItemSelect, renderItem, suffix, customAction, listBoxId = "autocomplete-listbox", listBoxAriaLabel, onEscape, onFocus, onBlur, size = "medium", supportText, errorText, invalid, required, inputRef: externalInputRef }) => {
	const [selectedIndex, setSelectedIndex] = useState(void 0);
	const [isFocused, setIsFocused] = useState(false);
	const internalInputRef = useRef(null);
	const inputRef = externalInputRef || internalInputRef;
	useEffect(() => {
		if (selectedIndex !== void 0 && selectedIndex >= items.length) setSelectedIndex(void 0);
	}, [items, selectedIndex]);
	const handleItemSelect = useCallback((item) => () => {
		onItemSelect(item);
		inputRef.current?.blur();
	}, [onItemSelect, inputRef]);
	const onKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			if (items.length > 0) setSelectedIndex(selectedIndex === void 0 || selectedIndex === items.length - 1 ? 0 : selectedIndex + 1);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			if (items.length > 0) setSelectedIndex(selectedIndex === void 0 || selectedIndex === 0 ? items.length - 1 : selectedIndex - 1);
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (selectedIndex !== void 0 && items[selectedIndex]) {
				onItemSelect(items[selectedIndex]);
				inputRef.current?.blur();
			}
		} else if (e.key === "Escape") {
			setSelectedIndex(void 0);
			onEscape?.();
		} else setSelectedIndex(void 0);
	};
	const handleContainerBlur = useCallback((e) => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsFocused(false);
			onBlur?.();
		}
	}, [onBlur]);
	const handleContainerFocus = useCallback(() => {
		setIsFocused(true);
		onFocus?.();
	}, [onFocus]);
	const handleMouseDown = useCallback((e) => {
		e.preventDefault();
	}, []);
	const showDropdown = searchTerm !== "" && isFocused;
	{
		const textInputProps = {
			id,
			placeholder,
			disabled,
			value: searchTerm,
			onChangeText: (text) => {
				onSearchChange({ target: { value: text } });
			},
			onKeyPress: onKeyDown,
			size,
			supportText,
			errorText,
			invalid,
			required,
			autoComplete: "off",
			role: "combobox",
			"aria-autocomplete": "list",
			"aria-expanded": showDropdown ? "true" : "false",
			"aria-controls": listBoxId
		};
		if (!hideLabel && label) textInputProps.title = label;
		else if (label) textInputProps.ariaLabel = label;
		else textInputProps.ariaLabel = "Search input";
		if (showDropdown && (items.length > 0 || customAction)) textInputProps.frameProps = {
			borderBottomLeftRadius: 0,
			borderBottomRightRadius: 0,
			borderBottomWidth: 0
		};
		return /* @__PURE__ */ jsx(Box, {
			onFocus: handleContainerFocus,
			onBlur: handleContainerBlur,
			children: /* @__PURE__ */ jsxs(Box, {
				position: "relative",
				children: [/* @__PURE__ */ jsx(TextInput, {
					...textInputProps,
					ref: inputRef,
					children: suffix
				}), showDropdown && (items.length > 0 || customAction) && /* @__PURE__ */ jsxs(BricksNextListBox, {
					listBoxId,
					ariaLabel: listBoxAriaLabel,
					size,
					children: [items.map((item, index) => {
						const isSelected = selectedIndex === index;
						return /* @__PURE__ */ jsx(Box, {
							tag: "li",
							id: "autocomplete-options",
							role: "option",
							"aria-selected": isSelected || void 0,
							overflow: "hidden",
							children: /* @__PURE__ */ jsx(PressableBox, {
								zIndex: 2,
								justifyContent: "flex-start",
								onMouseDown: handleMouseDown,
								analyticsName: "",
								"aria-label": item.ariaLabel,
								animation: "accelerateDecelerateFastest",
								height: "100%",
								width: "100%",
								backgroundColor: isSelected ? "$backgroundStateHoverPrimary" : "$backgroundPrimary",
								hoverStyle: { backgroundColor: "$backgroundStateHoverPrimary" },
								onPress: handleItemSelect(item),
								pressStyle: { scale: .98 },
								padding: 10,
								children: renderItem(item, isSelected)
							})
						}, item.id);
					}), customAction]
				})]
			})
		});
	}
	return null;
};
//#endregion
export { AutocompleteInput as default };

//# sourceMappingURL=AutocompleteInput.mjs.map